import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import {
  PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS,
  type PagamentosInscritosLigaFiltradosData,
  type PagamentosInscritosLigaFiltradosVars,
} from '../../../services/pagamentoInscritosLigaService';
import { StatusPagamento } from '../../../types/pagamento';
import PaymentStatusChip from '../../../components/PaymentStatusChip';
import BoletoDetailDialog from '../../../components/liga/BoletoDetailDialog';
import { formatCurrency } from '../../../utils/money';
import type { PagamentoInscritosLiga } from '../../../types/pagamentoInscritosLiga';
import type { CategoriaTorneio } from '../../../services/torneioService';

const COLUMN_COUNT = 7;

const STATUS_OPTIONS: { label: string; value: StatusPagamento | null }[] = [
  { label: 'Todos', value: null },
  { label: 'Pago', value: 'PAGO' as StatusPagamento },
  { label: 'Pendente', value: 'PENDENTE' as StatusPagamento },
  { label: 'Cancelado', value: 'CANCELADO' as StatusPagamento },
  { label: 'Estornado', value: 'ESTORNADO' as StatusPagamento },
];

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

interface TournamentFinanceiroPagamentosTabProps {
  torneioId: string;
  ligaId: string;
  categorias: CategoriaTorneio[];
}

const TournamentFinanceiroPagamentosTab: React.FC<TournamentFinanceiroPagamentosTabProps> = ({
  torneioId,
  ligaId,
  categorias,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<StatusPagamento | null>(null);
  const [categoriaFilter, setCategoriaFilter] = useState<string | null>(null);
  const [selectedBoleto, setSelectedBoleto] = useState<PagamentoInscritosLiga | null>(null);

  const { data, loading, error } = useQuery<
    PagamentosInscritosLigaFiltradosData,
    PagamentosInscritosLigaFiltradosVars
  >(PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS, {
    variables: { ligaId, size: 9999 },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });

  // Client-side torneioId filter (same pattern as TournamentChargesTab)
  const allItems = useMemo(() => {
    const items = data?.pagamentosInscritosLigaFiltrados?.items ?? [];
    return items.filter((p) => p.categoriaTorneio?.torneio?.id === torneioId);
  }, [data, torneioId]);

  const categoriaOptions = useMemo(
    () =>
      categorias
        .map((cat) => ({ id: cat.id, nome: cat.nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome)),
    [categorias],
  );

  const filteredItems = useMemo(() => {
    let items = allItems;

    if (statusFilter) {
      items = items.filter((p) => p.status === statusFilter);
    }

    if (categoriaFilter) {
      items = items.filter((p) => p.categoriaTorneio?.id === categoriaFilter);
    }

    return items;
  }, [allItems, statusFilter, categoriaFilter]);

  const paginatedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setStatusFilter(null);
    setCategoriaFilter(null);
    setPage(0);
  };

  const linkStyle = { color: '#1976d2', textDecoration: 'none' as const };

  return (
    <>
      {/* Filter Bar */}
      <Box className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Categoria"
              size="small"
              fullWidth
              value={categoriaFilter || ''}
              onChange={(e) => {
                setCategoriaFilter(e.target.value || null);
                setPage(0);
              }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Todas</MenuItem>
              {categoriaOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nome}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={statusFilter || ''}
              onChange={(e) => {
                setStatusFilter((e.target.value || null) as StatusPagamento | null);
                setPage(0);
              }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value || ''}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '0 1 auto' }}>
            <Button variant="outlined" color="inherit" size="small" onClick={handleResetFilters}>
              Limpar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#6c757d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categoria</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="center">Inscritos</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Valor</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Vencimento</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Pagamento</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: COLUMN_COUNT }).map((__, cidx) => (
                    <TableCell key={cidx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="alert alert-danger mb-0" role="alert">
                    Erro ao carregar cobranças. Tente novamente.
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  Nenhuma cobrança encontrada.
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    {p.categoriaTorneio?.id ? (
                      <Link
                        to={`/tournament/${torneioId}/category/${p.categoriaTorneio.id}`}
                        style={linkStyle}
                      >
                        {p.categoriaTorneio.nome}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="center">{p.quantidadeInscritos}</TableCell>
                  <TableCell>{formatCurrency(p.valor)}</TableCell>
                  <TableCell>
                    <PaymentStatusChip status={p.status} />
                  </TableCell>
                  <TableCell>{formatDate(p.dataVencimento)}</TableCell>
                  <TableCell>{formatDate(p.dataPagamento)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => setSelectedBoleto(p)}
                      title="Ver detalhes"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </TableContainer>

      <BoletoDetailDialog
        open={selectedBoleto !== null}
        pagamento={selectedBoleto}
        onClose={() => setSelectedBoleto(null)}
      />
    </>
  );
};

export default TournamentFinanceiroPagamentosTab;
